import { useCallback, useEffect, useState } from "react";

import {
  api,
  type CalendarColor,
  type Label as LabelData,
  type Week,
} from "@/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ErrorAlert } from "@/components/error-alert";
import { PageSkeleton } from "@/components/page-skeleton";
import { WeekChart } from "@/components/week-days-chart";
import { formatMinutes } from "@/lib/color";
import { cn } from "@/lib/utils";
import { labelHex } from "@/lib/domain";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function shiftDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function rangeLabel(start: string, end: string): string {
  const s = new Date(`${start}T00:00`);
  const e = new Date(`${end}T00:00`);
  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  return `${s.toLocaleDateString([], opts)} – ${e.toLocaleDateString([], { ...opts, year: "numeric" })}`;
}

export function WeekPage() {
  const [start, setStart] = useState<string | undefined>(undefined);
  const [week, setWeek] = useState<Week | null>(null);
  const [labels, setLabels] = useState<LabelData[]>([]);
  const [colors, setColors] = useState<CalendarColor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (anchor?: string) => {
    setLoading(true);
    try {
      const [w, labelData] = await Promise.all([
        api.week(anchor),
        api.labels(),
      ]);
      setWeek(w);
      setLabels(labelData.labels);
      setError(null);
    } catch (err) {
      setError(messageOf(err, "could not load the week"));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load(start);
  }, [load, start]);

  useEffect(() => {
    (async () => {
      try {
        setColors((await api.colors()).colors);
      } catch {
        // Colours are decoration.
      }
    })();
  }, []);

  if (error && !week) return <ErrorAlert message={error} />;
  if (!week) return <PageSkeleton />;

  const labelById = new Map(labels.map((l) => [l.id, l]));
  const diary = week.days.filter((d) => d.diary_note.trim() !== "");

  // Actual share per Label with its Ideal Day share alongside. Labels with an
  // ideal but no time this week still appear, so the gap is visible.
  type Row = {
    id: string;
    name: string;
    hex?: string;
    minutes: number;
    percent: number;
    ideal: number | null;
  };
  const rows: Row[] = week.by_label.map((r) => {
    const label = r.label_id ? labelById.get(r.label_id) : undefined;
    return {
      id: r.label_id || "unlabelled",
      name: label?.name ?? (r.label_id ? "Deleted Label" : "Not labeled"),
      hex: label ? labelHex(colors, label) : undefined,
      minutes: r.minutes,
      percent: r.percent,
      ideal: label && label.ideal_percent > 0 ? label.ideal_percent : null,
    };
  });
  for (const label of labels) {
    if (label.ideal_percent > 0 && !rows.some((r) => r.id === label.id)) {
      rows.push({
        id: label.id,
        name: label.name,
        hex: labelHex(colors, label),
        minutes: 0,
        percent: 0,
        ideal: label.ideal_percent,
      });
    }
  }
  const isCurrentWeek = week.days.some((d) => d.is_today);

  return (
    <div className="grid gap-4">
      {error && <ErrorAlert message={error} />}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Retrospective</CardTitle>
            <CardDescription>
              {rangeLabel(week.start, week.end)}. A look back at how time was
              actually spent, no targets.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={() => setStart(shiftDays(week.start, -7))}
            >
              ← Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={loading || isCurrentWeek}
              onClick={() => setStart(undefined)}
            >
              This week
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={() => setStart(shiftDays(week.start, 7))}
            >
              Next →
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <WeekChart days={week.days} />
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>Time by Label</CardTitle>
            <CardDescription>
              {week.total_minutes > 0
                ? `${formatMinutes(week.total_minutes)} of lived time. Each bar is how much of the Label's Ideal Day target the week reached; the mark is the target. An Activity counts fully under every Label it carries.`
                : "Nothing counted yet. Activities count once they are Done in a Planning Session, or once they have passed."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No time recorded this week.
              </p>
            ) : (
              <ul className="space-y-2.5">
                {rows.map((row) => (
                  <li key={row.id}>
                    <TargetBar row={row} />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>Diary</CardTitle>
            <CardDescription>Notes from the days you reviewed.</CardDescription>
          </CardHeader>
          <CardContent>
            {diary.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No diary entries this week.
              </p>
            ) : (
              <ul className="space-y-3">
                {diary.map((d) => (
                  <li key={d.date}>
                    <p className="text-xs font-medium text-muted-foreground">
                      {new Date(`${d.date}T00:00`).toLocaleDateString([], {
                        weekday: "long",
                        day: "numeric",
                        month: "short",
                      })}
                      {d.score !== null && ` · ${d.score}/10`}
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm">
                      {d.diary_note}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * One compact row per Label: name, lived time, and a bar of how much of
 * the Ideal Day target the week reached. The bar's scale runs to twice the
 * target, with a tick at 100%, so both shortfall and overshoot are visible
 * without a second bar. Labels with no target show their share of the
 * week instead.
 */
function TargetBar({
  row,
}: {
  row: {
    name: string;
    hex?: string;
    minutes: number;
    percent: number;
    ideal: number | null;
  };
}) {
  const attainment =
    row.ideal !== null && row.ideal > 0
      ? Math.round((row.percent / row.ideal) * 100)
      : null;
  // Scale: 0–200% of target; the tick sits at the target (50% of the track).
  const width =
    attainment === null
      ? Math.min(100, row.percent)
      : Math.min(100, attainment / 2);
  const over = attainment !== null && attainment > 100;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full bg-muted-foreground"
          style={row.hex ? { backgroundColor: row.hex } : undefined}
          aria-hidden
        />
        <span className="min-w-0 truncate font-medium">{row.name}</span>
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {formatMinutes(row.minutes)}
        </span>
      </div>
      <span
        className={cn(
          "shrink-0 text-right text-xs tabular-nums",
          over ? "font-medium" : "text-muted-foreground",
        )}
        title={
          attainment === null
            ? `${row.percent}% of the week's lived time; no Ideal Day target`
            : `${row.percent}% of the week against a ${row.ideal}% target`
        }
      >
        {attainment === null
          ? `${row.percent}% of week`
          : `${attainment}% of target`}
      </span>
      <div
        className="relative col-span-2 h-2 rounded-full bg-muted/60"
        aria-hidden
      >
        <div
          className="h-full rounded-full bg-muted-foreground"
          style={{
            width: `${width}%`,
            ...(row.hex ? { backgroundColor: row.hex } : {}),
          }}
        />
        {attainment !== null && (
          <span
            className="absolute -top-1 left-1/2 h-4 w-0.5 -translate-x-1/2 rounded bg-foreground/60"
            title={`Target: ${row.ideal}% of the week`}
          />
        )}
      </div>
    </div>
  );
}
